import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Menu, X, Check, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import EmailGate, { hasPreviewAccess } from "../components/EmailGate";
import ExerciseBody from "../components/ExerciseBody";

function SetPasswordNudge() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("sd_pw_nudge_dismissed") === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem("sd_pw_nudge_dismissed", "1"); } catch (_) {}
    setDismissed(true);
  };
  return (
    <div
      className="mb-10 flex flex-wrap items-start gap-3 justify-between"
      style={{
        padding: "14px 18px",
        background: "rgba(154,123,63,0.08)",
        border: "1px solid var(--gold-soft)",
        borderRadius: "2px",
      }}
      data-testid="set-password-nudge"
    >
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-1" style={{ color: "var(--gold)" }}>
          save your spot
        </div>
        <p className="text-sm" style={{ color: "var(--ink)" }}>
          you're signed in. set a password so you can come back without the email next time.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Link
          to="/auth/set-password"
          className="text-sm underline"
          style={{ color: "var(--ink)", textUnderlineOffset: "4px" }}
          data-testid="set-password-nudge-cta"
        >
          set a password →
        </Link>
        <button
          onClick={dismiss}
          data-testid="set-password-nudge-dismiss"
          aria-label="dismiss"
          style={{ color: "var(--ink-soft)", fontSize: 13, background: "transparent", border: "none", cursor: "pointer" }}
        >
          not now
        </button>
      </div>
    </div>
  );
}

function readingMinutes(html) {
  if (!html) return 1;
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  return Math.max(1, Math.round(words / 220));
}

export default function Course() {
  const { lessonId } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [current, setCurrent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [buyBusy, setBuyBusy] = useState(false);

  const loadList = async () => {
    const { data } = await api.get("/lessons");
    setLessons(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      const list = await loadList();
      let target = lessonId ? list.find((l) => l.id === lessonId) : null;
      if (!target) {
        if (user?.has_purchased || user?.is_admin) {
          target = list.find((l) => l.id === user.last_lesson_id) || list[0];
        } else {
          target = list.find((l) => l.is_free) || list[0];
        }
      }
      if (target) {
        await loadLesson(target.id, list);
        if (!lessonId) nav(`/course/${target.id}`, { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, [lessonId]);

  const loadLesson = async (id, listArg) => {
    try {
      const { data } = await api.get(`/lessons/${id}`);
      setCurrent(data);
      setSidebarOpen(false);
    } catch (e) {
      const list = listArg || lessons;
      const stub = list.find((l) => l.id === id);
      setCurrent(stub ? { ...stub, unlocked: false } : null);
    }
  };

  const completedCount = useMemo(() => lessons.filter((l) => l.completed).length, [lessons]);
  const total = lessons.length || 1;
  const pct = Math.round((completedCount / total) * 100);

  const currentIdx = lessons.findIndex((l) => l.id === current?.id);
  const prev = currentIdx > 0 ? lessons[currentIdx - 1] : null;
  const next = currentIdx >= 0 && currentIdx < lessons.length - 1 ? lessons[currentIdx + 1] : null;

  const isCompleted = lessons.find((l) => l.id === current?.id)?.completed;
  const hasAccess = user?.has_purchased || user?.is_admin;
  const isFreePreview = current?.is_free && !hasAccess;
  const isChapter02 = current?.slug === "chapter-02" || current?.slug === "chapter-02-practice";
  const [previewOk, setPreviewOk] = useState(hasPreviewAccess());

  // One-time bypass link for people who already have chapter 02 (PDF list).
  // Sharing e.g. /course/chapter-02?preview=1 skips the email gate for them.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview") === "1") {
        localStorage.setItem("sd_preview_ok", "1");
        setPreviewOk(true);
        // Clean the URL so the flag doesn't stick in shared/copied links.
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (_) {}
  }, []);

  const needsEmailGate = isChapter02 && !hasAccess && !previewOk;
  const [practiceDone, setPracticeDone] = useState(false);
  const readTime = useMemo(() => readingMinutes(current?.body), [current?.body]);
  const isLastLesson = currentIdx === lessons.length - 1 && lessons.length > 0;
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    setPracticeDone(Boolean(current && (user?.practice_done || []).includes(current.id)));
  }, [current, user]);

  const markComplete = async () => {
    if (!user) { nav("/login"); return; }
    try {
      const { data } = await api.post("/progress", { lesson_id: current.id, completed: !isCompleted });
      await loadList();
      await refresh();
      toast.success(isCompleted ? "unmarked" : "marked complete");
      if (!isCompleted && isLastLesson && (data.completed_lessons || []).length >= total) {
        setShowCelebration(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save progress");
    }
  };

  const togglePracticeDone = async () => {
    const nextVal = !practiceDone;
    setPracticeDone(nextVal);
    try {
      await api.post("/progress/practice", { lesson_id: current.id, done: nextVal });
      await refresh();
    } catch (e) {
      setPracticeDone(!nextVal);
      toast.error("could not save");
    }
  };

  const startCheckout = async () => {
    if (!user) { nav("/signup?next=buy"); return; }
    setBuyBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "checkout failed");
      setBuyBusy(false);
    }
  };

  const captureLead = async (e) => {
    e.preventDefault();
    if (!leadEmail) return;
    try {
      await api.post("/leads", { email: leadEmail });
      toast.success("thank you");
      setLeadEmail("");
    } catch { toast.error("could not save"); }
  };

  return (
    <div className="min-h-screen" data-testid="course-page">
      {/* progress bar */}
      <div className="w-full h-[3px]" style={{ background: "var(--cream-2)" }}>
        <div
          className="h-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: "var(--gold)" }}
          data-testid="progress-bar"
        />
      </div>

      <div className="max-w-6xl mx-auto flex relative">
        {/* Sidebar */}
        <aside
          className={`fixed md:sticky top-0 md:top-16 left-0 h-screen md:h-[calc(100vh-4rem)] w-72 z-40 md:z-auto overflow-y-auto sidebar-scroll px-6 py-6 transition-transform md:transition-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
          style={{ background: "var(--cream)", borderRight: "1px solid var(--gold-soft)" }}
          data-testid="sidebar"
        >
          <div className="flex items-center justify-between mb-6 md:hidden">
            <span className="font-serif text-lg">lessons</span>
            <button onClick={() => setSidebarOpen(false)} data-testid="sidebar-close">
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--gold)" }}>
            {completedCount}/{total} · {pct}% complete
          </p>
          <nav className="space-y-1" data-testid="sidebar-nav">
            {lessons.map((l, i) => {
              const locked = !l.unlocked;
              const active = l.id === current?.id;
              const isPractice = l.is_practice;
              return (
                <button
                  key={l.id}
                  onClick={() => nav(`/course/${l.id}`)}
                  data-testid={`sidebar-lesson-${i}`}
                  className={`w-full text-left flex items-start gap-2 py-2 pr-2 transition-colors ${isPractice ? "pl-6" : "pl-2"}`}
                  style={{
                    background: active ? "var(--cream-2)" : "transparent",
                    color: active ? "var(--ink)" : "var(--ink-soft)",
                    borderLeft: isPractice ? "1px solid var(--gold-soft)" : "none",
                    marginLeft: isPractice ? "0.75rem" : "0",
                  }}
                >
                  <span className="mt-1 shrink-0">
                    {l.completed ? (
                      <Check size={14} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
                    ) : locked ? (
                      <Lock size={12} strokeWidth={1.5} style={{ color: "var(--ink-faint)" }} />
                    ) : isPractice ? (
                      <span className="font-serif text-[10px] uppercase tracking-widest" style={{ color: "var(--gold)" }}>
                        do
                      </span>
                    ) : (
                      <span className="font-serif text-xs" style={{ color: "var(--gold)" }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}
                  </span>
                  <span className={`leading-snug flex-1 ${isPractice ? "text-xs italic" : "text-sm"}`}>{l.title}</span>
                  {!locked && !l.completed && (
                    <span className="text-[10px]" style={{ color: "var(--ink-faint)" }}>
                      {readingMinutes(l.body) || "—"}m
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-[80vh] px-5 md:px-12 py-10 md:py-16">
          <button
            className="md:hidden mb-6 flex items-center gap-2 text-sm"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle"
            style={{ color: "var(--ink-soft)" }}
          >
            <Menu size={18} strokeWidth={1.5} /> lessons
          </button>

          {current ? (
            <article className="max-w-2xl mx-auto" data-testid="lesson-article">
              {/* Prompt auto-provisioned users (quiz/free-chapter) to set a password */}
              {user?.auto_provisioned && !user?.password_set && (
                <SetPasswordNudge />
              )}
              <p className="text-xs uppercase tracking-[0.3em] mb-4 flex items-center gap-3" style={{ color: "var(--gold)" }}>
                <span>
                  {current.is_practice
                    ? "the practice"
                    : current.is_free
                    ? "free chapter"
                    : `lesson ${currentIdx + 1} of ${total}`}
                </span>
                {current.unlocked && current.body && (
                  <>
                    <span style={{ color: "var(--gold-soft)" }}>·</span>
                    <span data-testid="read-time">{readTime} min read</span>
                  </>
                )}
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl mb-10" style={{ color: "var(--ink)" }} data-testid="lesson-title">
                {current.title}
              </h1>

              {needsEmailGate ? (
                <EmailGate
                  onUnlock={() => setPreviewOk(true)}
                  headline={current.slug === "chapter-02-practice" ? "one more thing." : "before you read."}
                  subhead={
                    current.slug === "chapter-02-practice"
                      ? "leave your name and email and the practice opens too."
                      : undefined
                  }
                />
              ) : current.unlocked ? (
                current.is_practice ? (
                  <div
                    className="relative"
                    style={{
                      background: "var(--cream-2)",
                      border: "1px solid var(--gold-soft)",
                      padding: "2rem 1.75rem",
                      borderRadius: "2px",
                    }}
                    data-testid="lesson-body"
                  >
                    <span
                      className="absolute -top-3 left-6 px-3 text-[10px] uppercase tracking-[0.3em]"
                      style={{ background: "var(--cream)", color: "var(--gold)" }}
                    >
                      do this
                    </span>
                    <ExerciseBody html={current.body || "<p><em>practice content coming soon.</em></p>"} lessonId={current.id} unlocked={true} />
                  </div>
                ) : (
                  <ExerciseBody
                    html={current.body || "<p><em>content coming soon.</em></p>"}
                    lessonId={current.id}
                    unlocked={true}
                  />
                )
              ) : (
                <div className="py-16 text-center border-y" style={{ borderColor: "var(--gold-soft)" }}>
                  <Lock size={24} strokeWidth={1} className="mx-auto mb-4" style={{ color: "var(--gold)" }} />
                  <p className="mb-6" style={{ color: "var(--ink-soft)" }}>
                    this lesson is part of the paid course.
                  </p>
                  <button onClick={startCheckout} disabled={buyBusy} className="btn-primary" data-testid="locked-buy">
                    unlock  $150 cad
                  </button>
                </div>
              )}

              {/* Free preview footer CTA — simplified when they've already given us email via the gate */}
              {isFreePreview && current.unlocked && !needsEmailGate && (
                <div className="mt-14 pt-10 border-t" style={{ borderColor: "var(--gold-soft)" }} data-testid="free-cta">
                  <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--gold)" }}>
                    keep reading
                  </p>
                  <h3 className="font-serif text-2xl sm:text-3xl mb-4" style={{ color: "var(--ink)" }}>
                    twelve more chapters wait inside.
                  </h3>
                  <p className="mb-6" style={{ color: "var(--ink-soft)" }}>
                    one payment. lifetime access. read on any device.
                  </p>
                  <button onClick={startCheckout} disabled={buyBusy} className="btn-primary" data-testid="free-cta-buy">
                    enroll  $150 cad
                  </button>
                </div>
              )}

              {/* Prev / Mark / Next — hidden while the gate is up */}
              {!needsEmailGate && (
              <>
              {/* practice-done row */}
              {current.unlocked && hasAccess && current.is_practice && (
                <div className="mt-10 flex flex-wrap items-center gap-3 border-t pt-5" style={{ borderColor: "var(--gold-soft)" }}>
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="practice-done-label">
                    <input
                      type="checkbox"
                      checked={practiceDone}
                      onChange={togglePracticeDone}
                      data-testid="practice-done"
                      style={{ accentColor: "var(--gold)" }}
                    />
                    <span style={{ color: "var(--ink-soft)" }}>i did the practice</span>
                  </label>
                </div>
              )}
              <div className="mt-8 flex items-center justify-between gap-4 border-t pt-6" style={{ borderColor: "var(--gold-soft)" }}>
                <button
                  disabled={!prev}
                  onClick={() => prev && nav(`/course/${prev.id}`)}
                  className="flex items-center gap-2 text-sm disabled:opacity-30"
                  style={{ color: "var(--ink-soft)" }}
                  data-testid="nav-prev"
                >
                  <ChevronLeft size={16} strokeWidth={1.5} /> previous
                </button>
                {current.unlocked && (
                  <button
                    onClick={markComplete}
                    className={isCompleted ? "btn-ghost" : "btn-primary"}
                    data-testid="mark-complete"
                  >
                    {isCompleted ? "unmark" : "mark complete"}
                  </button>
                )}
                <button
                  disabled={!next}
                  onClick={() => next && nav(`/course/${next.id}`)}
                  className="flex items-center gap-2 text-sm disabled:opacity-30"
                  style={{ color: "var(--ink-soft)" }}
                  data-testid="nav-next"
                >
                  next <ChevronRight size={16} strokeWidth={1.5} />
                </button>
              </div>
              </>
              )}
            </article>
          ) : (
            <div className="text-center py-20" style={{ color: "var(--ink-soft)" }}>loading…</div>
          )}
        </main>
      </div>

      {showCelebration && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: "rgba(38, 51, 31, 0.75)" }}
          data-testid="celebration-overlay"
          onClick={() => setShowCelebration(false)}
        >
          <div
            className="max-w-lg w-full p-10 text-center"
            style={{ background: "var(--cream)", border: "1px solid var(--gold)", borderRadius: "2px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--gold)" }}>the workshop, complete</p>
            <h2 className="font-serif text-4xl sm:text-5xl mb-4" style={{ color: "var(--ink)" }}>you did it.</h2>
            <p className="mb-6" style={{ color: "var(--ink-soft)" }}>
              all thirteen lessons and every practice. carry it with you.
            </p>
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText("i just finished showgirl desire."); toast.success("copied"); } catch {}
              }}
              className="btn-primary"
              data-testid="celebration-copy"
            >
              copy: &ldquo;i just finished showgirl desire.&rdquo;
            </button>
            <div className="mt-4">
              <button onClick={() => setShowCelebration(false)} className="text-sm underline" style={{ color: "var(--ink-soft)" }} data-testid="celebration-close">
                close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
